import { useEffect, useState, useCallback } from "react";
import { Link, Navigate } from "react-router-dom";
import { api, fileUrl, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Search, ShieldCheck, ShieldOff, Trash2, Pencil, X, BadgeCheck, Users, Crown, AlertTriangle, UserPlus, Copy, Upload, Download } from "lucide-react";

const FILTERS = [
  { key: "", label: "All" },
  { key: "unverified_requests", label: "Verification requests" },
  { key: "premium", label: "Premium" },
  { key: "verified", label: "Verified" },
  { key: "banned", label: "Banned" },
];

export default function AdminUsers() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState(false);
  const [stats, setStats] = useState(null);

  const fetchStats = useCallback(async () => {
    try { const { data } = await api.get("/admin/stats"); setStats(data); } catch {}
  }, []);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users", { params: { q: q || undefined, status: status || undefined, limit: 100 } });
      setItems(data.users); setTotal(data.total);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [q, status]);

  useEffect(() => { if (user?.role === "admin") { load(); fetchStats(); } }, [user, load, fetchStats]);

  if (!user) return null;
  if (user.role !== "admin") return <Navigate to="/dashboard" replace />;

  const downloadAll = async () => {
    try {
      const { data } = await api.get("/admin/users", { params: { limit: 10000 } });
      const cols = ["full_name","email","gender","age","city","religion","community","education","profession","verified","is_premium","banned","created_at"];
      const escape = (v) => {
        if (v == null) return "";
        const s = String(v).replace(/"/g, '""');
        return /[",\n]/.test(s) ? `"${s}"` : s;
      };
      const rows = [cols.join(","), ...data.users.map((u) => cols.map((c) => escape(u[c])).join(","))].join("\n");
      const blob = new Blob([rows], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `sanyog_users_backup_${new Date().toISOString().slice(0,10)}.csv`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${data.users.length} users`);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const act = async (id, action, extra = {}) => {
    try {
      if (action === "delete") {
        if (!window.confirm("Delete this user permanently? All their data will be lost.")) return;
        await api.delete(`/admin/users/${id}`);
        toast.success("User deleted");
      } else if (action === "ban") {
        await api.post(`/admin/users/${id}/ban`);
        toast.success("User banned");
      } else if (action === "unban") {
        await api.post(`/admin/users/${id}/unban`);
        toast.success("User unbanned");
      } else if (action === "verify") {
        await api.post(`/admin/users/${id}/verify`);
        toast.success("User verified");
      } else if (action === "unverify") {
        await api.put(`/admin/users/${id}`, { verified: false });
        toast.success("Verification removed");
      } else if (action === "premium_grant") {
        await api.put(`/admin/users/${id}`, { is_premium: true });
        toast.success("Premium granted");
      } else if (action === "premium_revoke") {
        await api.put(`/admin/users/${id}`, { is_premium: false });
        toast.success("Premium revoked");
      }
      load(); fetchStats();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-16" data-testid="admin-users-page">
      <p className="overline mb-3">Admin · Users</p>
      <h1 className="font-serif-display text-4xl md:text-5xl tracking-tight">Manage all users.</h1>
      <p className="mt-3 text-sm text-[#8C857B]">Search, edit, verify, ban, or delete any profile. Changes take effect instantly.</p>

      <div className="mt-6 flex flex-wrap gap-2">
        <button onClick={() => setCreating(true)} data-testid="admin-add-user-btn"
          className="rounded-full bg-[#C85A44] hover:bg-[#A84A36] text-white px-5 py-2.5 text-sm inline-flex items-center gap-2 transition">
          <UserPlus className="h-4 w-4" strokeWidth={1.5}/> Add new user (free)
        </button>
        <button onClick={() => setImporting(true)} data-testid="admin-import-csv-btn"
          className="rounded-full bg-[#2C2A28] hover:bg-black text-white px-5 py-2.5 text-sm inline-flex items-center gap-2 transition">
          <Upload className="h-4 w-4" strokeWidth={1.5}/> Bulk import (CSV)
        </button>
        <button onClick={downloadAll} data-testid="admin-download-all-btn"
          className="rounded-full border border-[#E2DCD0] hover:bg-[#F2EFE9] text-[#2C2A28] px-5 py-2.5 text-sm inline-flex items-center gap-2 transition">
          <Download className="h-4 w-4" strokeWidth={1.5}/> Download all users (backup)
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="admin-stats">
          <StatTile label="Users" value={stats.total_users} icon={<Users className="h-3.5 w-3.5"/>} />
          <StatTile label="Verified" value={stats.verified} icon={<BadgeCheck className="h-3.5 w-3.5 text-[#2E5C46]"/>} />
          <StatTile label="Premium" value={stats.premium} icon={<Crown className="h-3.5 w-3.5 text-[#C85A44]"/>} />
          <StatTile label="Banned" value={stats.banned} icon={<ShieldOff className="h-3.5 w-3.5 text-[#9C3A3A]"/>} />
          <StatTile label="Verify reqs" value={stats.verify_requests} tone={stats.verify_requests ? "warn" : ""} />
          <StatTile label="Reports" value={stats.open_reports} tone={stats.open_reports ? "warn" : ""} link="/admin/reports" />
          <StatTile label="Matches" value={stats.matches} />
        </div>
      )}

      {/* Search + filters */}
      <div className="mt-10 flex flex-col md:flex-row gap-3 md:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8C857B]" strokeWidth={1.5}/>
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()}
            data-testid="admin-search-input" placeholder="Search email, name, or city…"
            className="w-full rounded-xl border border-[#E2DCD0] bg-white pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C85A44]/30"/>
        </div>
        <div className="flex gap-1 p-1 rounded-full bg-[#F2EFE9] flex-wrap">
          {FILTERS.map((f) => (
            <button key={f.key} onClick={() => setStatus(f.key)}
              data-testid={`admin-filter-${f.key || "all"}`}
              className={`px-3 py-1.5 rounded-full text-xs transition ${status === f.key ? "bg-white shadow-sm" : "text-[#8C857B]"}`}>
              {f.label}
            </button>
          ))}
        </div>
        <button onClick={load} className="rounded-full bg-[#2C2A28] hover:bg-black text-white px-5 py-2.5 text-sm transition">Search</button>
      </div>

      {/* User list */}
      <div className="mt-8">
        {loading ? (
          <div className="text-center py-24 text-[#8C857B]">Loading…</div>
        ) : items.length === 0 ? (
          <div className="text-center py-24 border border-dashed border-[#E2DCD0] rounded-2xl text-[#8C857B]">No users match.</div>
        ) : (
          <>
            <p className="text-xs text-[#8C857B] mb-4">{items.length} of {total} users</p>
            <div className="space-y-3">
              {items.map((u) => {
                const cover = u.photos?.[0] ? fileUrl(u.photos[0]) : null;
                return (
                  <div key={u.id} data-testid={`admin-user-${u.id}`}
                    className={`p-4 md:p-5 rounded-2xl border ${u.banned ? "border-[#9C3A3A]/30 bg-[#FAE5E0]/30" : "border-[#E2DCD0] bg-white"}`}>
                    <div className="flex items-center gap-4 flex-wrap">
                      <div className="relative w-14 h-14 rounded-xl overflow-hidden bg-[#F2EFE9] flex-shrink-0">
                        {cover ? <img src={cover} alt="" className="absolute inset-0 w-full h-full object-cover"/> :
                          <div className="absolute inset-0 flex items-center justify-center font-serif-display text-lg text-[#8C857B]">{u.full_name?.[0] || "?"}</div>}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-serif-display text-lg">{u.full_name || "(no name)"}</span>
                          {u.verified && <span className="text-xs bg-[#E8F0EA] text-[#2E5C46] px-2 py-0.5 rounded-full inline-flex items-center gap-1"><BadgeCheck className="h-3 w-3"/>Verified</span>}
                          {u.is_premium && <span className="text-xs bg-[#FAE5E0] text-[#C85A44] px-2 py-0.5 rounded-full">Premium</span>}
                          {u.banned && <span className="text-xs bg-[#9C3A3A]/10 text-[#9C3A3A] px-2 py-0.5 rounded-full">Banned</span>}
                          {u.verification_requested && !u.verified && <span className="text-xs bg-[#F5E8C7] text-[#8a6d1a] px-2 py-0.5 rounded-full">Verify request</span>}
                          {u.role === "admin" && <span className="text-xs bg-[#2C2A28] text-white px-2 py-0.5 rounded-full">ADMIN</span>}
                        </div>
                        <div className="text-xs text-[#8C857B] mt-1 truncate">
                          {u.email} · {u.gender} · {u.age ? `${u.age}y` : "?y"} · {u.city || "—"} · {u.religion || "—"}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        {u.role !== "admin" && <>
                          <Link to={`/profile/${u.id}`} className="text-xs text-[#8C857B] hover:text-[#C85A44]">View</Link>
                          <button onClick={() => setEditing(u)} data-testid={`admin-edit-${u.id}`} className="rounded-full bg-[#F2EFE9] hover:bg-[#E2DCD0] p-2 transition" title="Edit">
                            <Pencil className="h-3.5 w-3.5" strokeWidth={1.5}/>
                          </button>
                          {u.verified ?
                            <button onClick={() => act(u.id, "unverify")} data-testid={`admin-unverify-${u.id}`} className="rounded-full bg-[#F2EFE9] hover:bg-[#E2DCD0] p-2 transition" title="Unverify">
                              <BadgeCheck className="h-3.5 w-3.5 text-[#8C857B]" strokeWidth={1.5}/>
                            </button> :
                            <button onClick={() => act(u.id, "verify")} data-testid={`admin-verify-${u.id}`} className="rounded-full bg-[#E8F0EA] hover:bg-[#d5e6dc] p-2 transition" title="Verify">
                              <ShieldCheck className="h-3.5 w-3.5 text-[#2E5C46]" strokeWidth={1.5}/>
                            </button>}
                          {u.banned ?
                            <button onClick={() => act(u.id, "unban")} data-testid={`admin-unban-${u.id}`} className="rounded-full bg-[#F2EFE9] hover:bg-[#E2DCD0] px-3 py-1.5 text-xs transition">Unban</button> :
                            <button onClick={() => act(u.id, "ban")} data-testid={`admin-ban-${u.id}`} className="rounded-full bg-[#FAE5E0] hover:bg-[#F5D2C9] text-[#9C3A3A] p-2 transition" title="Ban">
                              <ShieldOff className="h-3.5 w-3.5" strokeWidth={1.5}/>
                            </button>}
                          <button onClick={() => act(u.id, "delete")} data-testid={`admin-delete-${u.id}`} className="rounded-full hover:bg-[#FAE5E0] text-[#9C3A3A] p-2 transition" title="Delete">
                            <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5}/>
                          </button>
                        </>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {editing && <EditModal user={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {creating && <CreateModal onClose={() => setCreating(false)} onCreated={() => { setCreating(false); load(); fetchStats(); }} />}
      {importing && <ImportModal onClose={() => setImporting(false)} onImported={() => { load(); fetchStats(); }} />}
    </div>
  );
}

function StatTile({ label, value, icon, tone, link }) {
  const cls = `p-4 rounded-2xl border ${tone === "warn" ? "border-[#C85A44]/40 bg-[#FAE5E0]/30" : "border-[#E2DCD0] bg-white"}`;
  const content = (
    <div className={cls}>
      <div className="flex items-center gap-1.5 mb-2">
        {icon}
        <span className="text-[10px] uppercase tracking-widest text-[#8C857B]">{label}</span>
      </div>
      <div className="font-serif-display text-2xl">{value ?? 0}</div>
    </div>
  );
  return link ? <Link to={link}>{content}</Link> : content;
}

function EditModal({ user, onClose, onSaved }) {
  const [form, setForm] = useState({
    full_name: user.full_name || "",
    email: user.email || "",
    gender: user.gender || "male",
    date_of_birth: user.date_of_birth?.slice(0, 10) || "",
    religion: user.religion || "",
    community: user.community || "",
    city: user.city || "",
    country: user.country || "",
    education: user.education || "",
    profession: user.profession || "",
    about_me: user.about_me || "",
  });
  const [saving, setSaving] = useState(false);
  const upd = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));
  const save = async () => {
    setSaving(true);
    try {
      const payload = Object.fromEntries(Object.entries(form).filter(([, v]) => v !== ""));
      await api.put(`/admin/users/${user.id}`, payload);
      toast.success("User updated");
      onSaved();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="admin-edit-modal">
        <div className="flex items-center justify-between p-6 border-b border-[#E2DCD0]">
          <div>
            <p className="overline">Edit user</p>
            <h3 className="font-serif-display text-2xl mt-1">{user.full_name}</h3>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-[#F2EFE9]"><X className="h-4 w-4"/></button>
        </div>
        <div className="p-6 space-y-4">
          {[
            ["full_name", "Full name"], ["email", "Email"], ["date_of_birth", "Date of birth (YYYY-MM-DD)"],
            ["religion", "Religion"], ["community", "Community"], ["city", "City"], ["country", "Country"],
            ["education", "Education"], ["profession", "Profession"],
          ].map(([k, label]) => (
            <div key={k}>
              <label className="block text-xs uppercase tracking-widest text-[#8C857B] mb-1.5">{label}</label>
              <input value={form[k]} onChange={upd(k)} data-testid={`admin-field-${k}`}
                className="w-full rounded-xl border border-[#E2DCD0] bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C85A44]/30"/>
            </div>
          ))}
          <div>
            <label className="block text-xs uppercase tracking-widest text-[#8C857B] mb-1.5">About</label>
            <textarea rows={3} value={form.about_me} onChange={upd("about_me")}
              className="w-full rounded-xl border border-[#E2DCD0] bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C85A44]/30 resize-none"/>
          </div>
        </div>
        <div className="flex justify-end gap-2 p-6 border-t border-[#E2DCD0]">
          <button onClick={onClose} className="rounded-full border border-[#E2DCD0] hover:bg-[#F2EFE9] px-5 py-2.5 text-sm">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="admin-edit-save"
            className="rounded-full bg-[#C85A44] hover:bg-[#A84A36] disabled:opacity-60 text-white px-5 py-2.5 text-sm">
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}


const inp = "w-full rounded-xl border border-[#E2DCD0] bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C85A44]/30";

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-widest text-[#8C857B] mb-1.5">{label}</label>
      {children}
    </div>
  );
}

function CreateModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    full_name: "", email: "", gender: "male", date_of_birth: "",
    religion: "", community: "", mother_tongue: "", city: "", country: "India",
    education: "", profession: "", about_me: "", grant_premium_days: 0,
  });
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(null);
  const upd = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const save = async () => {
    if (!form.full_name || !form.email || !form.gender) {
      toast.error("Full name, email and gender are required");
      return;
    }
    setSaving(true);
    try {
      const clean = Object.fromEntries(Object.entries(form).filter(([, v]) => v !== "" && v !== 0));
      clean.full_name = form.full_name;
      clean.email = form.email;
      clean.gender = form.gender;
      if (form.grant_premium_days) clean.grant_premium_days = parseInt(form.grant_premium_days, 10);
      const { data } = await api.post("/admin/users", clean);
      setCreated(data);
      toast.success("User created");
      onCreated();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const copyCreds = async () => {
    const text = `Sanyog Login\nEmail: ${created.email}\nPassword: ${created.temp_password}\n\nLogin at: ${window.location.origin}/login`;
    try { await navigator.clipboard.writeText(text); toast.success("Credentials copied"); }
    catch { toast.error("Please copy manually"); }
  };

  if (created) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
        <div className="bg-white rounded-3xl max-w-md w-full p-8 text-center" onClick={(e) => e.stopPropagation()} data-testid="admin-created-modal">
          <UserPlus className="h-10 w-10 text-[#2E5C46] mx-auto mb-4" strokeWidth={1.25}/>
          <h3 className="font-serif-display text-2xl mb-2">User created!</h3>
          <p className="text-sm text-[#8C857B] mb-6">Share these credentials with the user on WhatsApp.</p>
          <div className="p-4 rounded-xl bg-[#F2EFE9] border border-[#E2DCD0] text-left mb-4 space-y-1 text-sm font-mono break-all">
            <div><span className="text-[#8C857B]">Email:</span> {created.email}</div>
            <div><span className="text-[#8C857B]">Password:</span> <strong className="text-[#C85A44]" data-testid="temp-password">{created.temp_password}</strong></div>
          </div>
          <button onClick={copyCreds} data-testid="copy-creds-btn"
            className="w-full rounded-full bg-[#C85A44] hover:bg-[#A84A36] text-white px-5 py-2.5 text-sm inline-flex items-center justify-center gap-2 transition mb-2">
            <Copy className="h-4 w-4"/> Copy for WhatsApp
          </button>
          <button onClick={onClose} className="w-full rounded-full border border-[#E2DCD0] hover:bg-[#F2EFE9] px-5 py-2.5 text-sm transition">Done</button>
          <p className="text-xs text-[#8C857B] mt-4">Ask them to change the password after first login.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="admin-create-modal">
        <div className="flex items-center justify-between p-6 border-b border-[#E2DCD0]">
          <div>
            <p className="overline">Add user manually</p>
            <h3 className="font-serif-display text-2xl mt-1">Onboard someone for free</h3>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-[#F2EFE9]"><X className="h-4 w-4"/></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Full name *"><input value={form.full_name} onChange={upd("full_name")} data-testid="create-full-name" className={inp}/></Field>
            <Field label="Email *"><input type="email" value={form.email} onChange={upd("email")} data-testid="create-email" className={inp}/></Field>
            <Field label="Gender *">
              <select value={form.gender} onChange={upd("gender")} data-testid="create-gender" className={inp}>
                <option value="male">Man</option><option value="female">Woman</option>
              </select>
            </Field>
            <Field label="Date of birth"><input type="date" value={form.date_of_birth} onChange={upd("date_of_birth")} data-testid="create-dob" className={inp}/></Field>
            <Field label="Religion"><input value={form.religion} onChange={upd("religion")} className={inp}/></Field>
            <Field label="Community"><input value={form.community} onChange={upd("community")} className={inp}/></Field>
            <Field label="Mother tongue"><input value={form.mother_tongue} onChange={upd("mother_tongue")} className={inp}/></Field>
            <Field label="City"><input value={form.city} onChange={upd("city")} data-testid="create-city" className={inp}/></Field>
            <Field label="Country"><input value={form.country} onChange={upd("country")} className={inp}/></Field>
            <Field label="Education"><input value={form.education} onChange={upd("education")} className={inp}/></Field>
            <Field label="Profession"><input value={form.profession} onChange={upd("profession")} className={inp}/></Field>
          </div>
          <Field label="About">
            <textarea rows={3} value={form.about_me} onChange={upd("about_me")} className={inp + " resize-none"}/>
          </Field>
          <Field label="Grant free Premium (days) — optional launch bonus">
            <input type="number" min={0} max={365} value={form.grant_premium_days} onChange={upd("grant_premium_days")}
              data-testid="create-grant-days" className={inp} placeholder="e.g. 30 for one month free"/>
          </Field>
          <div className="p-3 rounded-xl bg-[#F2EFE9] text-xs text-[#5d574f]">
            💡 Password will be auto-generated — you'll see it after creating and can share via WhatsApp.
          </div>
        </div>
        <div className="flex justify-end gap-2 p-6 border-t border-[#E2DCD0]">
          <button onClick={onClose} className="rounded-full border border-[#E2DCD0] hover:bg-[#F2EFE9] px-5 py-2.5 text-sm">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="create-user-submit"
            className="rounded-full bg-[#C85A44] hover:bg-[#A84A36] disabled:opacity-60 text-white px-5 py-2.5 text-sm">
            {saving ? "Creating…" : "Create user"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ImportModal({ onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [grantDays, setGrantDays] = useState(0);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const upload = async () => {
    if (!file) { toast.error("Please choose a CSV file"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/admin/users/bulk-import?grant_premium_days=${grantDays || 0}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      toast.success(`Imported ${data.summary.created} users`);
      onImported();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const downloadTemplate = () => {
    const csv = [
      "full_name,email,gender,date_of_birth,religion,community,mother_tongue,city,country,education,profession,about_me",
      "Ravi Deshpande,ravi@example.com,male,1990-03-15,Hindu,Brahmin,Marathi,Pune,India,MBA,Software Engineer,Family-oriented and career-focused",
      "Priya Sharma,priya@example.com,female,1993-07-22,Hindu,Maratha,Marathi,Mumbai,India,MSc,Teacher,Loves reading and music",
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "sanyog_users_template.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const downloadCreated = () => {
    if (!result?.created?.length) return;
    const rows = [
      "full_name,email,temp_password",
      ...result.created.map((r) => `"${r.full_name.replace(/"/g,'""')}","${r.email}","${r.temp_password}"`),
    ].join("\n");
    const blob = new Blob([rows], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `sanyog_created_users_${Date.now()}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="admin-import-modal">
        <div className="flex items-center justify-between p-6 border-b border-[#E2DCD0]">
          <div>
            <p className="overline">Bulk import</p>
            <h3 className="font-serif-display text-2xl mt-1">Upload CSV of users</h3>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-[#F2EFE9]"><X className="h-4 w-4"/></button>
        </div>

        {!result ? (
          <>
            <div className="p-6 space-y-5">
              <div className="p-4 rounded-xl bg-[#F2EFE9] border border-[#E2DCD0]">
                <p className="text-sm text-[#5d574f] mb-3">
                  <strong>Step 1:</strong> Download the template, fill in your users (name, email, gender required), then upload.
                </p>
                <button onClick={downloadTemplate} data-testid="download-template-btn"
                  className="rounded-full bg-white border border-[#E2DCD0] hover:bg-[#F9F8F6] px-4 py-2 text-xs inline-flex items-center gap-2">
                  <Download className="h-3.5 w-3.5"/> Download template
                </button>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-[#8C857B] mb-1.5">CSV file</label>
                <input type="file" accept=".csv,.tsv,.txt" onChange={(e) => setFile(e.target.files?.[0] || null)}
                  data-testid="import-file-input"
                  className="w-full rounded-xl border border-[#E2DCD0] bg-white px-4 py-2.5 text-sm file:mr-3 file:rounded-full file:border-0 file:bg-[#2C2A28] file:text-white file:px-4 file:py-1.5"/>
                {file && <p className="mt-2 text-xs text-[#8C857B]">Selected: {file.name} ({(file.size/1024).toFixed(1)} KB)</p>}
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-[#8C857B] mb-1.5">Grant free Premium (days) — optional</label>
                <input type="number" min={0} max={365} value={grantDays} onChange={(e) => setGrantDays(e.target.value)}
                  data-testid="import-grant-days"
                  className="w-full rounded-xl border border-[#E2DCD0] bg-white px-4 py-2.5 text-sm"
                  placeholder="e.g. 30 for one-month launch bonus for all imported users"/>
              </div>
              <div className="p-3 rounded-xl bg-[#FAE5E0]/40 text-xs text-[#5d574f]">
                💡 Required columns: <strong>full_name, email, gender</strong> (male/female). Duplicate emails skipped.
              </div>
            </div>
            <div className="flex justify-end gap-2 p-6 border-t border-[#E2DCD0]">
              <button onClick={onClose} className="rounded-full border border-[#E2DCD0] hover:bg-[#F2EFE9] px-5 py-2.5 text-sm">Cancel</button>
              <button onClick={upload} disabled={busy || !file} data-testid="import-upload-btn"
                className="rounded-full bg-[#C85A44] hover:bg-[#A84A36] disabled:opacity-60 text-white px-5 py-2.5 text-sm inline-flex items-center gap-2">
                <Upload className="h-4 w-4"/> {busy ? "Importing…" : "Upload & import"}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <ResultTile label="Created" value={result.summary.created} tone="ok"/>
                <ResultTile label="Skipped" value={result.summary.skipped} tone="warn"/>
                <ResultTile label="Errors" value={result.summary.errors} tone={result.summary.errors ? "bad" : ""}/>
              </div>
              {result.created?.length > 0 && (
                <div>
                  <button onClick={downloadCreated} data-testid="download-created-btn"
                    className="w-full rounded-full bg-[#2E5C46] hover:opacity-90 text-white px-4 py-2.5 text-sm inline-flex items-center justify-center gap-2 transition">
                    <Download className="h-4 w-4"/> Download all logins (CSV with passwords)
                  </button>
                  <p className="text-xs text-[#8C857B] mt-2">⚠️ Save this file — passwords are shown only once. Share with each user via WhatsApp.</p>
                </div>
              )}
              {result.skipped?.length > 0 && (
                <details className="rounded-xl bg-[#F5E8C7]/30 border border-[#F5E8C7] p-3">
                  <summary className="text-xs font-medium cursor-pointer">Skipped ({result.skipped.length}) — already exist</summary>
                  <ul className="mt-2 text-xs text-[#5d574f] space-y-1">
                    {result.skipped.map((s, i) => <li key={i}>Row {s.row}: {s.email}</li>)}
                  </ul>
                </details>
              )}
              {result.errors?.length > 0 && (
                <details className="rounded-xl bg-[#FAE5E0] border border-[#9C3A3A]/20 p-3">
                  <summary className="text-xs font-medium text-[#9C3A3A] cursor-pointer">Errors ({result.errors.length})</summary>
                  <ul className="mt-2 text-xs text-[#9C3A3A] space-y-1">
                    {result.errors.map((e, i) => <li key={i}>Row {e.row}: {e.email || "(no email)"} — {e.reason}</li>)}
                  </ul>
                </details>
              )}
            </div>
            <div className="flex justify-end gap-2 p-6 border-t border-[#E2DCD0]">
              <button onClick={() => { setResult(null); setFile(null); }} className="rounded-full border border-[#E2DCD0] hover:bg-[#F2EFE9] px-5 py-2.5 text-sm">Import more</button>
              <button onClick={onClose} className="rounded-full bg-[#C85A44] hover:bg-[#A84A36] text-white px-5 py-2.5 text-sm">Done</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function ResultTile({ label, value, tone }) {
  const cls = tone === "ok" ? "bg-[#E8F0EA] text-[#2E5C46]" :
              tone === "warn" ? "bg-[#F5E8C7]/40 text-[#8a6d1a]" :
              tone === "bad" ? "bg-[#FAE5E0] text-[#9C3A3A]" :
              "bg-[#F2EFE9] text-[#2C2A28]";
  return (
    <div className={`p-4 rounded-xl text-center ${cls}`}>
      <div className="font-serif-display text-3xl">{value}</div>
      <div className="text-[10px] uppercase tracking-widest mt-1">{label}</div>
    </div>
  );
}

